import type { User } from '../features/chat/chat.types';
import { clearFusionNowTokenCache, clearMacAIChatTokenCache } from './fusionNowAuth';
import { clearLocalStorageKeepingWidgets } from '../utils/clearSessionStorage';

export interface SignupData {
  AgencyName: string;
  PrimaryContactName: string;
  AddressLine1: string;
  AddressLine2: string;
  Phone: string;
  Fax: string;
  EMail: string;
  ZipCode: string;
  StateName: string;
  City: string;
  LicenseNumber: string;
  DomesticLicenseStateName: string;
  CellPhone: string;
  Password: string;
  ConfirmPassword: string;
}

export interface NoSubscriptionData {
  fullName: string;
  agencyName: string;
  email: string;
  agencyId?: string;
  agentId?: string;
}

export class NoSubscriptionError extends Error {
  data: NoSubscriptionData;
  constructor(message: string, data: NoSubscriptionData) {
    super(message);
    this.name = 'NoSubscriptionError';
    this.data = data;
  }
}

export async function login(email: string, password: string): Promise<User> {
  const response = await fetch(`${process.env.REACT_APP_Auth_Url}/api/account/signin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ workemail: email, password }),
  });

  if (!response.ok) {
    if (response.status === 403) {
      const body = await response.json().catch(() => ({}));
      throw new NoSubscriptionError(body?.message || 'No active subscription found.', {
        fullName: body?.fullName ?? '',
        agencyName: body?.agencyName ?? '',
        email: body?.email ?? '',
        agencyId: body?.agencyId,
        agentId: body?.agentId,
      });
    }
    if (response.status === 401) {
      throw new Error('Invalid email or password');
    }
    throw new Error('Sign in failed. Please try again.');
  }

  const data = await response.json();

  const firstName = data.firstName ?? '';
  const lastName  = data.lastName  ?? '';
  const fullName  = [firstName, lastName].filter(Boolean).join(' ') || data.userName || email;

  localStorage.setItem('authToken', data.authToken ?? '');
  localStorage.setItem('userEmail', email.toLowerCase());
  localStorage.setItem('amsToken', data.amsToken ?? '');
  localStorage.setItem('userName', data.userName ?? '');
  if (data.userId != null) localStorage.setItem('userId', String(data.userId));
  localStorage.setItem('firstName', firstName);
  localStorage.setItem('lastName', lastName);
  localStorage.setItem('agency', data.agencyName ?? '');
  localStorage.setItem('NowcertsAgentId', data.nowCertsAgentId ?? '');
  localStorage.setItem('NCAgencyId', data.nowCertsAgencyId ?? '');
  localStorage.setItem('isAMSUser', String(data.isAMSUser ?? false));
  localStorage.setItem('isSuperUser', String(data.isSuperUser ?? false));
  localStorage.setItem('isAgencyOwner', String(data.isAgencyOwner ?? false));
  localStorage.setItem('showWelcomeChannel', String(data.showWelcomeChannel ?? true));
  localStorage.setItem('nowCertsUserId', data.nowCertsUserId ?? '');
  if (data.agencyDomain) localStorage.setItem('agencyDomain', data.agencyDomain);
  localStorage.setItem('firstName', data.firstName ?? '');
  localStorage.setItem('lastName', data.lastName ?? '');
  if (data.themeMode === 1 || data.themeMode === 2) {
    // When source is Rate, always keep light theme — don't override with server preference
    if (localStorage.getItem('sourceTool') !== 'Rate') {
      localStorage.setItem('momentum-theme', data.themeMode === 1 ? 'dark' : 'light');
    }
  }

  return {
    id: data.nowCertsAgentId || `user-${Date.now()}`,
    name: fullName,
    email,
    role: 'agent',
    isAMSUser: data.isAMSUser === true,
  };
}

export async function signup(data: SignupData): Promise<User> {
  const response = await fetch(`${process.env.REACT_APP_Auth_Url}/api/account/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(errorBody || 'Signup failed. Please try again.');
  }

  // Auto-login after successful signup
  return login(data.EMail, data.Password);
}

export async function logout(): Promise<void> {
  clearLocalStorageKeepingWidgets();
  clearFusionNowTokenCache();
  clearMacAIChatTokenCache(); // also clear MAC AI Chat token — derived from FN token
}

export function getIsAMSUser(): boolean {
  return localStorage.getItem('isAMSUser') === 'true';
}

export async function getCurrentUser(): Promise<User | null> {
  const authToken = localStorage.getItem('authToken');
  if (!authToken) return null;

  const firstName = localStorage.getItem('firstName') ?? '';
  const lastName  = localStorage.getItem('lastName')  ?? '';
  const fullName  = [firstName, lastName].filter(Boolean).join(' ') || localStorage.getItem('userName') || 'User';

  return {
    id: localStorage.getItem('NowcertsAgentId') || 'user-restored',
    name: fullName,
    email: '',
    role: 'agent',
    isAMSUser: localStorage.getItem('isAMSUser') === 'true',
  };
}
